import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Image, Loader2, Hash, Award, Trophy } from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { useToast } from '../../../components/ui/use-toast';
import { supabase } from '../../../lib/supabase';
import { PublicHeader } from '../../../components/layout/PublicHeader';
import { PublicFooter } from '../../../components/layout/PublicFooter';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';

// Service pour l'inscription des candidats
const submitContestantRegistration = async (data: {
  name: string;
  description: string;
  unique_code: string;
  contest_id: string;
  photo_url: string;
  status: string;
}) => {
  try {
    console.log('Données à insérer:', data);
    
    // Utiliser une requête fetch directe avec les en-têtes appropriés
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/public_contestants_submissions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_KEY}`,
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        unique_code: data.unique_code,
        contest_id: data.contest_id,
        photo_url: data.photo_url,
        status: data.status,
        created_at: new Date().toISOString()
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Erreur d\'insertion:', errorData);
      throw errorData;
    }
    
    console.log('Insertion réussie');
    return { success: true };
  } catch (error) {
    console.error('Erreur:', error);
    return { success: false, error };
  }
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const registrationSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  description: z.string().min(1, 'La description est requise'),
  image: z
    .instanceof(FileList)
    .refine((files) => files.length > 0, 'Une photo est requise')
    .refine((files) => files[0].size <= MAX_FILE_SIZE, 'La taille maximale du fichier est de 5MB')
    .refine(
      (files) => ACCEPTED_IMAGE_TYPES.includes(files[0].type),
      'Format accepté : .jpg, .jpeg, .png, .webp'
    ),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Vous devez accepter les conditions de participation',
  }),
});

type RegistrationFormValues = z.infer<typeof registrationSchema>;

const ContestRegistration = () => {
  const { contestCode } = useParams<{ contestCode: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contest, setContest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      name: '',
      description: '',
      acceptTerms: false,
    },
  });

  useEffect(() => {
    const fetchContest = async () => {
      setLoading(true);
      try {
        // Rechercher le concours par son code unique
        const { data, error } = await supabase
          .from('contests')
          .select('*')
          .eq('code_unique', contestCode)
          .single();

        if (error) throw error;

        if (!data) {
          navigate('/not-found');
          return;
        }

        setContest(data);
      } catch (error) {
        console.error('Erreur lors de la récupération du concours:', error);
        navigate('/not-found');
      } finally {
        setLoading(false);
      }
    };

    if (contestCode) {
      fetchContest();
    } else {
      navigate('/not-found');
    }
  }, [contestCode, navigate]);

  const generateUniqueCode = (contestCode: string, candidateName: string): string => {
    const normalizedName = candidateName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
      .replace(/[^a-z0-9]/g, '-') // Remplacer les caractères non alphanumériques par des tirets
      .replace(/-+/g, '-') // Remplacer les séquences de tirets par un seul tiret
      .replace(/^-|-$/g, ''); // Supprimer les tirets au début et à la fin

    return `${contestCode}-${normalizedName}`;
  };

  const uploadImage = async (file: File, uniqueCode: string): Promise<string> => {
    try {
      // Convertir l'image en base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Erreur lors de la conversion de l\'image:', error);
      throw error;
    }
  };

  const onSubmit = async (data: RegistrationFormValues) => {
    if (!contest) return;

    try {
      setSubmitting(true);

      // Générer le code unique pour le candidat
      const uniqueCode = generateUniqueCode(contestCode!, data.name);
      
      // Convertir l'image en base64
      const imageBase64 = await uploadImage(data.image[0], uniqueCode);

      // Insérer le candidat avec le statut "pending" et l'image en base64
      const result = await submitContestantRegistration({
        name: data.name,
        description: data.description,
        unique_code: uniqueCode,
        contest_id: contest.id,
        photo_url: imageBase64,
        status: 'pending'
      });

      if (!result.success) throw result.error;

      // Réinitialiser le formulaire et afficher un message de succès
      form.reset();
      setImagePreview(null);
      setSuccess(true);
      
      toast({
        title: 'Inscription réussie',
        description: 'Votre candidature a été soumise avec succès et sera examinée par notre équipe.',
      });
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'inscription. Veuillez réessayer.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen bg-[#111216] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
        </div>
        <PublicFooter />
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <SEOMetaTags
        title={`Inscription - ${contest?.name || 'Concours'} | ERNEST NZOGOUG`}
        description={`Inscrivez-vous au concours ${contest?.name || ''} sur ERNEST NZOGOUG. Participez et tentez de gagner de nombreux prix !`}
        image={contest?.image_url || 'https://nzofoug.com/contest-default.jpg'}
        url={`https://nzofoug.com/inscription/${contestCode}`}
      />
      
      <div className="bg-[#111216] text-white relative overflow-hidden pt-20">
        {/* Background Image avec Overlay et Parallaxe */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.rawpixel.com/image_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDI0LTA4L3N0YXJ0dXBpbWFnZXNfZ29sZF9nbGl0dGVyX2dvbGRfY29uZmV0dGlfYm9rZWhfc3BhcmtsZV9zaW1wbGVfZ185MTEzYzJkMy03YjEyLTQwYmQtYjNhYS04OTVlOGU4MmM5N2NfMi5qcGc.jpg)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        {/* Hero Section */}
        <div className="relative min-h-[40vh] overflow-hidden flex items-center">
          {/* Contenu du Hero */}
          <div className="relative z-20 container mx-auto px-4">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-block p-2 bg-yellow-400/10 rounded-xl mb-4">
                {contest?.logo_url ? (
                  <img src={contest.logo_url} alt={`Logo ${contest.name}`} className="w-32 h-32mx-auto rounded-full object-cover" />
                ) : (
                  <Trophy className="w-32 h-32 mx-auto text-yellow-400" />
                )}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-200 to-yellow-400 text-transparent bg-clip-text">
                Inscription au concours {contest?.name}
              </h1>
              <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
                Remplissez le formulaire ci-dessous pour soumettre votre candidature. Notre équipe examinera votre demande dans les plus brefs délais.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="relative z-10 container mx-auto px-4 py-12">
          <motion.div 
            className="max-w-2xl mx-auto bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-white/10 shadow-xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {success ? (
              <motion.div 
                className="text-center py-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-block p-4 bg-green-500/20 rounded-full mb-6">
                  <Award className="h-16 w-16 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4 text-green-400">Candidature soumise avec succès !</h2>
                <p className="text-gray-300 mb-8">
                  Votre candidature a été enregistrée et sera examinée par notre équipe. Vous recevrez une notification une fois qu'elle aura été traitée.
                </p>
                <Button 
                  onClick={() => setSuccess(false)} 
                  className="bg-gradient-to-r from-yellow-500 to-yellow-700 hover:from-yellow-600 hover:to-yellow-800 text-white"
                >
                  Soumettre une autre candidature
                </Button>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <motion.div variants={itemVariants}>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Nom du candidat</FormLabel>
                          <FormControl>
                            <Input placeholder="Entrez le nom complet" {...field} className="bg-black/50 border-white/20 text-white" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Présentez-vous brièvement"
                              {...field}
                              className="min-h-[120px] bg-black/50 border-white/20 text-white"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <FormField
                      control={form.control}
                      name="image"
                      render={({ field: { value, onChange, ...fieldProps } }) => (
                        <FormItem>
                          <FormLabel className="text-white">Photo du candidat</FormLabel>
                          <FormControl>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                              <div className="flex items-center justify-center border-2 border-dashed border-white/20 rounded-lg p-4 bg-black/50">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  id="image-upload"
                                  onChange={(e) => {
                                    onChange(e.target.files);
                                    handleImageChange(e);
                                  }}
                                  {...fieldProps}
                                />
                                <label
                                  htmlFor="image-upload"
                                  className="cursor-pointer flex flex-col items-center justify-center w-full h-full"
                                >
                                  <Image className="h-8 w-8 text-gray-400 mb-2" />
                                  <span className="text-sm text-gray-400">Cliquez pour sélectionner une image</span>
                                  <span className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP (max 5MB)</span>
                                </label>
                              </div>
                              {imagePreview && (
                                <div className="relative aspect-square rounded-lg overflow-hidden bg-black/30">
                                  <img
                                    src={imagePreview}
                                    alt="Aperçu"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <FormField
                      control={form.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-white">
                              J'accepte les conditions de participation et la politique de confidentialité
                            </FormLabel>
                            <FormDescription className="text-gray-400 text-xs">
                              En soumettant ce formulaire, vous acceptez que vos informations soient utilisées dans le cadre du concours.
                            </FormDescription>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="pt-4">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-yellow-500 to-yellow-700 hover:from-yellow-600 hover:to-yellow-800 text-white"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Soumission en cours...
                        </>
                      ) : (
                        'Soumettre ma candidature'
                      )}
                    </Button>
                  </motion.div>
                </form>
              </Form>
            )}
          </motion.div>
        </div>
      </div>
      
      <PublicFooter />
    </>
  );
};

export default ContestRegistration;
